import type { EventInput } from '@fullcalendar/core';
import { formatIsoDate, subtractDay } from '@src/shared/lib';

export function getDateOnly(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function toCalendarInputDate(value: EventInput['start']) {
  if (value instanceof Date) {
    return value;
  }

  if (Array.isArray(value)) {
    const [year, month = 0, day = 1, hour = 0, minute = 0, second = 0] = value;
    return new Date(year, month, day, hour, minute, second);
  }

  if (value == null) {
    return new Date(Number.NaN);
  }

  return new Date(value);
}

export function doesBackgroundEventCoverDate(
  event: EventInput,
  resourceId: string,
  date: Date,
) {
  if (event.display !== 'background' || event.resourceId !== resourceId) {
    return false;
  }

  if ('daysOfWeek' in event && Array.isArray(event.daysOfWeek)) {
    return event.daysOfWeek.includes(date.getDay());
  }

  if (!event.start) {
    return false;
  }

  const dateMs = getDateOnly(date).getTime();
  const startMs = getDateOnly(toCalendarInputDate(event.start)).getTime();
  const endMs = event.end
    ? getDateOnly(subtractDay(toCalendarInputDate(event.end), 1)).getTime()
    : startMs;

  return startMs <= dateMs && endMs >= dateMs;
}

export function createWeekendBackgroundEvents(
  resourceIds: string[],
): EventInput[] {
  return resourceIds.flatMap((resourceId) => [
    {
      id: `weekend-bg-${resourceId}-0`,
      resourceId,
      daysOfWeek: [0],
      display: 'background' as const,
      classNames: ['fc-weekend-background-event', 'fc-weekend-sunday'],
    },
    {
      id: `weekend-bg-${resourceId}-6`,
      resourceId,
      daysOfWeek: [6],
      display: 'background' as const,
      classNames: ['fc-weekend-background-event', 'fc-weekend-saturday'],
    },
  ]);
}

export function createSingleDayBackgroundEvents(
  resourceIds: string[],
  date: Date,
  className: string,
  idPrefix: string,
  existingBackgroundEvents: EventInput[] = [],
): EventInput[] {
  const targetDate = getDateOnly(date);
  const targetDateIso = formatIsoDate(targetDate);

  return resourceIds.flatMap((resourceId) => {
    const hasExistingBackground = existingBackgroundEvents.some((event) =>
      doesBackgroundEventCoverDate(event, resourceId, targetDate),
    );

    if (hasExistingBackground) {
      return [];
    }

    return [
      {
        id: `${idPrefix}-${resourceId}-${targetDateIso}`,
        resourceId,
        start: targetDateIso,
        end: formatIsoDate(subtractDay(targetDate, -1)),
        display: 'background' as const,
        classNames: [className],
      },
    ];
  });
}
